import type {
  ProjectCandidateSource,
  ProjectStage,
  SourcingExplorationSummary,
  SourcingProjectCandidateView,
  SourcingProjectView,
} from "@shared/labs/sourcing";
import type { BlockedCompanyInput } from "@shared/labs/sourcing-project-config";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Ban,
  Check,
  ChevronDown,
  Clock,
  Download,
  ExternalLink,
  FolderKanban,
  History,
  Layers,
  Link2,
  Loader2,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Star,
  Target,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";
import { CandidateCard, judgeVerdicts } from "../candidate-card";
import { Chip } from "../chip";
import { CreateSetDialog } from "../create-set-dialog";
import {
  downloadProjectJson,
  filterProjectRows,
  formatTenure,
  resolveCollisions,
  shortLocation,
} from "../lib";
import { QueryInterpretation, yearsLabel } from "../query-summary";
import { AddUrlsDialog } from "./add-urls-dialog";
import { CollisionBoard } from "./collision-board";
import { PickFromRunDialog } from "./pick-from-run-dialog";

const POLL_INTERVAL_MS = 2000;
// biome-ignore lint/security/noSecrets: route deep-link literal, not a credential
const PROJECTS_TAB_URL = "/labs/sourcing?tab=projects";
/** Level-Assessment evaluation sets are capped at 20 candidates. */
const MAX_SET = 20;
const INGEST_TOP_N_OPTIONS = [10, 25, 50, 100];

const STAGE_META: Record<ProjectStage, { title: string; hint: string }> = {
  sourced: {
    title: "Sourced",
    hint: "Chegaram de sessões e adições manuais — triagem pendente.",
  },
  shortlisted: {
    title: "Shortlisted",
    hint: "Os escolhidos da vaga — envie para o Level Assessment ou exporte.",
  },
  rejected: { title: "Rejected", hint: "Fora da vaga — ficam registrados e nunca reentram." },
};

const TRIAGE_STAGES: ProjectStage[] = ["sourced", "shortlisted", "rejected"];

const SESSION_STATUS: Record<string, string> = {
  pending: "na fila",
  running: "explorando",
  harvesting: "colhendo",
  done: "concluída",
  error: "erro",
};

function hasPendingWork(view: SourcingProjectView): boolean {
  return (
    view.candidates.some((c) => c.fetchStatus === "fetching" || c.fetchStatus === "judging") ||
    view.sessions.some(
      (s) => s.status === "pending" || s.status === "running" || s.status === "harvesting",
    )
  );
}

/** FIT-descending among ready rows; pending rows sink to the end (they have no verdict yet). */
function sortForDisplay(rows: SourcingProjectCandidateView[]): SourcingProjectCandidateView[] {
  return [...rows].sort((a, b) => {
    const ra = a.fetchStatus === "ready" ? 1 : 0;
    const rb = b.fetchStatus === "ready" ? 1 : 0;
    if (ra !== rb) return rb - ra;
    return (b.judgement?.score ?? 0) - (a.judgement?.score ?? 0);
  });
}

/**
 * A sourcing PROJECT — the tracker for one open position. Aggregates the
 * position's sessions (explore/precise runs whose top-N auto-ingest) plus
 * manual adds, all judged against the project's canonical brief and triaged
 * through the Sourced → Shortlisted → Rejected board (a segmented switcher over
 * a master-detail list). Every way of ADDING candidates is consolidated in the
 * "Sessões de busca" card header (new explore/precise session · bring from a
 * past run · URL import). Viewing/polling is free; the only paid action on this
 * page is the URL import (estimate-gated). Read-only for non-owners opening a
 * shared link.
 */
export default function SourcingProjectDetail() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [, params] = useRoute("/labs/sourcing/projects/:projectId");
  const projectId = params?.projectId ?? "";

  const [view, setView] = useState<SourcingProjectView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = useRef(true);

  const [urlsOpen, setUrlsOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  // When set, the pick dialog opens straight onto this run (the "Selecionar
  // candidatos" CTA a finished session shows); null → the run-list picker.
  const [pickRunId, setPickRunId] = useState<string | null>(null);
  // Candidates that collided on the last add, shown in the drag-to-re-triage
  // board. Empty → board closed.
  const [collisions, setCollisions] = useState<SourcingProjectCandidateView[]>([]);
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [editingName, setEditingName] = useState<string | null>(null);
  // The two reference cards fold independently; both open by default so nothing
  // is hidden on load. Collapsing them declutters the page for triage-focused work.
  const [descOpen, setDescOpen] = useState(true);
  const [sessionsOpen, setSessionsOpen] = useState(true);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    try {
      const v: SourcingProjectView = await rpcClient.labs.sourcingProjectGet({ projectId });
      if (!aliveRef.current) return;
      setView(v);
      setLoadError(null);
      if (pollRef.current) clearTimeout(pollRef.current);
      // Poll only while something is in flight (fills or sessions) — a settled
      // project costs zero requests.
      if (hasPendingWork(v)) {
        pollRef.current = setTimeout(() => void refresh(), POLL_INTERVAL_MS);
      }
    } catch (err) {
      if (!aliveRef.current) return;
      setLoadError(err instanceof Error ? err.message : "Erro inesperado");
    }
  }, [projectId]);

  useEffect(() => {
    aliveRef.current = true;
    void refresh();
    return () => {
      aliveRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [refresh]);

  async function setStage(row: SourcingProjectCandidateView, stage: ProjectStage) {
    // Optimistic move; the next refresh reconciles.
    setView((prev) =>
      prev
        ? {
            ...prev,
            candidates: prev.candidates.map((c) => (c.id === row.id ? { ...c, stage } : c)),
          }
        : prev,
    );
    try {
      await rpcClient.labs.sourcingProjectSetStage({
        projectId,
        candidateRowId: row.id,
        stage,
      });
    } catch (err) {
      toast({
        title: "Falha ao mover candidato",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
      void refresh();
    }
  }

  async function removeErrorRow(row: SourcingProjectCandidateView) {
    try {
      await rpcClient.labs.sourcingProjectRemoveErrorCandidate({
        projectId,
        candidateRowId: row.id,
      });
      void refresh();
    } catch (err) {
      toast({
        title: "Falha ao remover",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    }
  }

  async function saveName() {
    if (!view || editingName === null) return;
    const name = editingName.trim();
    setEditingName(null);
    if (!name || name === view.name) return;
    setView({ ...view, name });
    try {
      await rpcClient.labs.sourcingProjectUpdate({ projectId, name });
    } catch (err) {
      toast({
        title: "Falha ao renomear",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
      void refresh();
    }
  }

  async function saveTopN(value: string) {
    const ingestTopN = Number(value);
    if (!view || !Number.isFinite(ingestTopN)) return;
    setView({ ...view, ingestTopN });
    try {
      await rpcClient.labs.sourcingProjectUpdate({ projectId, ingestTopN });
    } catch (err) {
      toast({
        title: "Falha ao ajustar o top-N",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
      void refresh();
    }
  }

  if (loadError) {
    return (
      <section className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-12 text-center">
        <p className="text-sm font-medium">Não foi possível abrir o projeto</p>
        <p className="max-w-md text-sm text-muted-foreground">{loadError}</p>
        <Button variant="outline" onClick={() => navigate(PROJECTS_TAB_URL)}>
          <ArrowLeft /> Voltar aos projetos
        </Button>
      </section>
    );
  }

  if (!view) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-10 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Carregando projeto…
      </div>
    );
  }

  const byStage = (stage: ProjectStage) =>
    sortForDisplay(view.candidates.filter((c) => c.stage === stage));
  const shortlisted = byStage("shortlisted");
  const shortlistReady = shortlisted.filter((c) => c.fetchStatus === "ready");
  const laCandidates = shortlistReady.slice(0, MAX_SET).map((c) => c.candidate);

  // After any add: refresh (new rows land), and if some candidates were already
  // in the project, open the collision board on them for quick re-triage.
  function onAdded(skippedCandidateIds: string[]) {
    void refresh();
    if (!view) return;
    const rows = resolveCollisions(skippedCandidateIds, view.candidates);
    if (rows.length > 0) setCollisions(rows);
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(PROJECTS_TAB_URL)}
            className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft size={12} /> Projetos · Sourcing
          </button>
          {editingName === null ? (
            <div className="flex items-center gap-2">
              <PageHeader
                title={view.name}
                icon={<FolderKanban className="h-5 w-5 text-muted-foreground" />}
              />
              <button
                type="button"
                onClick={() => setEditingName(view.name)}
                aria-label="Renomear projeto"
                className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-foreground/5"
              >
                <Pencil size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Input
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveName();
                  if (e.key === "Escape") setEditingName(null);
                }}
                maxLength={200}
                className="max-w-md"
                autoFocus
              />
              <Button size="sm" onClick={() => void saveName()}>
                <Check /> Salvar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingName(null)}>
                Cancelar
              </Button>
            </div>
          )}
        </div>
        <Button
          variant="outline"
          className="shrink-0"
          onClick={() => navigate(`/labs/sourcing/projects/${projectId}/customize`)}
        >
          <SlidersHorizontal /> Customizar Projeto
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {view.laScope && (
          <>
            <Chip>{view.laScope.modelName}</Chip>
            {view.laScope.disciplineLabel && <Chip>{view.laScope.disciplineLabel}</Chip>}
            {view.laScope.levelLabel && <Chip>{view.laScope.levelLabel}</Chip>}
          </>
        )}
        <span className="ml-1 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          Top
          <Select value={String(view.ingestTopN)} onValueChange={(v) => void saveTopN(v)}>
            <SelectTrigger className="h-6 w-[64px] px-2 text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INGEST_TOP_N_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          por FIT entram no projeto quando uma sessão termina
        </span>
      </div>

      {/* "Descrição" — the structured interpretation of the JD (the judge's
          canonical reference), not the raw text. Collapsible: folded, it shows
          just Cargo + Experiência so the header still reads at a glance. Falls
          back to the raw brief while the query hasn't materialized yet
          (raw-brief projects judge nothing until their first add). */}
      <Collapsible
        open={descOpen}
        onOpenChange={setDescOpen}
        className="mt-4 rounded-xl border border-border bg-card"
      >
        <CollapsibleTrigger className="group flex w-full items-center gap-2.5 px-5 py-4 text-left">
          <ChevronDown
            size={15}
            className="shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Descrição
          </span>
          {!descOpen && <CollapsedJobSummary view={view} />}
        </CollapsibleTrigger>
        <CollapsibleContent className="px-5 pb-5">
          {view.query ? (
            <QueryInterpretation query={view.query} />
          ) : (
            <>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Interpretação pendente (gerada na primeira avaliação)
              </span>
              <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-foreground/80">
                {view.brief}
              </p>
            </>
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Project settings — the per-project config layer: companies whose current
          employees are blocked from entering, plus a compact set of per-position
          overrides (an unset knob inherits the product-wide config live). */}
      <ProjectSettingsCard view={view} projectId={projectId} onSaved={() => void refresh()} />

      {/* Sessions strip — the position's runs (the brief-iteration history).
          Collapsible: the header keeps the add-candidate buttons visible even
          when folded (they're the page's primary action), while the run list
          itself is what collapses. */}
      <Collapsible
        open={sessionsOpen}
        onOpenChange={setSessionsOpen}
        className="mt-6 rounded-xl border border-border bg-card p-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CollapsibleTrigger className="group flex items-center gap-2.5 text-left">
            <ChevronDown
              size={14}
              className="shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90"
            />
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Sessões de busca
            </span>
            {!sessionsOpen && view.sessions.length > 0 && (
              <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                {view.sessions.length}
              </span>
            )}
          </CollapsibleTrigger>
          {/* EVERY way of adding candidates lives here — always visible, even
              collapsed: the two search flows (paid, run as sessions), bringing
              from a past run (free), and the manual URL import (paid per
              profile, estimate-gated). */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/labs/sourcing?project=${projectId}&kind=explore`)}
            >
              <Sparkles /> Nova exploração
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/labs/sourcing?project=${projectId}&kind=precise`)}
            >
              <Target /> Nova busca precisa
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setPickRunId(null);
                setPickOpen(true);
              }}
            >
              <History /> Trazer de uma exploração
            </Button>
            <Button size="sm" variant="outline" onClick={() => setUrlsOpen(true)}>
              <Link2 /> Adicionar por URL
            </Button>
          </div>
        </div>
        <CollapsibleContent>
          {(() => {
            // Manual-URL adds have no session object, so they're aggregated into
            // one synthetic card sitting alongside the real session rows.
            const manualRows = view.candidates.filter((c) => c.source === "manual-url");
            if (view.sessions.length === 0 && manualRows.length === 0) {
              return (
                <p className="mt-3 text-[13px] text-muted-foreground">
                  Nenhuma sessão ainda — rode uma exploração e selecione os candidatos que entram no
                  projeto, traga candidatos de uma busca antiga ou adicione perfis por URL.
                </p>
              );
            }
            return (
              <div className="mt-3 space-y-1.5">
                {view.sessions.map((s) => (
                  <SessionRow
                    key={s.id}
                    session={s}
                    onPick={() => {
                      setPickRunId(s.id);
                      setPickOpen(true);
                    }}
                  />
                ))}
                {manualRows.length > 0 && (
                  <ManualAddsCard rows={manualRows} usd={view.manualUrlUsd} />
                )}
              </div>
            );
          })()}
        </CollapsibleContent>
      </Collapsible>

      {/* Shortlist actions. */}
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          size="sm"
          onClick={() => setSetDialogOpen(true)}
          disabled={laCandidates.length === 0}
        >
          <Layers /> Avaliar shortlist no LA
          {laCandidates.length > 0 && (
            <span className="ml-1 tabular-nums opacity-80">({laCandidates.length})</span>
          )}
        </Button>
        <ExportJsonMenu view={view} />
      </div>

      {/* The triage board — one card with a Sourced → Shortlisted → Rejected
          segmented switcher over a master-detail split: the ranked list on the
          left, the selected candidate's full "why" on the right, plus a
          bulk-move keybar when rows are checked. */}
      <TriageBoard
        view={view}
        onMove={(row, stage) => void setStage(row, stage)}
        onRemoveError={(row) => void removeErrorRow(row)}
      />

      <AddUrlsDialog
        open={urlsOpen}
        onOpenChange={setUrlsOpen}
        projectId={projectId}
        onAdded={onAdded}
      />
      <PickFromRunDialog
        open={pickOpen}
        onOpenChange={(o) => {
          setPickOpen(o);
          if (!o) setPickRunId(null);
        }}
        projectId={projectId}
        ingestTopN={view.ingestTopN}
        initialExplorationId={pickRunId ?? undefined}
        onAdded={onAdded}
      />
      <CollisionBoard
        open={collisions.length > 0}
        onOpenChange={(o) => {
          if (!o) setCollisions([]);
        }}
        projectId={projectId}
        rows={collisions}
        onChanged={() => void refresh()}
      />
      <CreateSetDialog
        open={setDialogOpen}
        onOpenChange={setSetDialogOpen}
        candidates={laCandidates}
        prefillScope={{
          modelId: view.laModelId ?? null,
          disciplineId: view.laDisciplineId ?? null,
          level: view.laLevel ?? null,
        }}
        defaultName={`Projeto — ${view.name.slice(0, 60)}`}
      />
    </>
  );
}

/**
 * The folded "Descrição" header summary — Cargo + Experiência, straight off the
 * parsed query, so a collapsed card still says who the position is for. Renders a
 * muted "pending" note when the JD hasn't been interpreted yet (raw-brief project).
 */
function CollapsedJobSummary({ view }: { view: SourcingProjectView }) {
  const f = view.query?.filters;
  if (!f) {
    return <span className="text-xs italic text-muted-foreground/70">interpretação pendente</span>;
  }
  const levelChips = [f.seniority, view.query?.targetLevelLabel].filter(Boolean) as string[];
  const years = yearsLabel(f.minYearsExperience, f.maxYearsExperience);
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
      <span className="truncate font-medium text-foreground">{f.title ?? "a vaga"}</span>
      {levelChips.length > 0 && (
        <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-primary">
          {levelChips.join(" · ")}
        </span>
      )}
      {years && (
        <>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-muted-foreground">{years}</span>
        </>
      )}
    </span>
  );
}

/** Source flag for a triage row — where the candidate entered the project from. */
const SOURCE_META: Record<ProjectCandidateSource, { icon: typeof Sparkles; label: string }> = {
  exploration: { icon: Sparkles, label: "sessão" },
  "manual-pick": { icon: History, label: "trazido de busca" },
  "manual-url": { icon: Link2, label: "URL" },
};

/**
 * The triage board — the position's whole pipeline in one card. A
 * Sourced → Shortlisted → Rejected segmented switcher picks the visible stage;
 * a master-detail split shows the FIT-ranked list on the left and the selected
 * candidate's full analysis (the reused CandidateCard) on the right; a keybar
 * appears when rows are checked, for moving a whole batch at once. Every stage
 * transition — single or bulk — goes through the same `onMove` the buttons use,
 * so drag-free triage and the detail buttons stay in lockstep.
 */
function TriageBoard({
  view,
  onMove,
  onRemoveError,
}: {
  view: SourcingProjectView;
  onMove: (row: SourcingProjectCandidateView, stage: ProjectStage) => void;
  onRemoveError: (row: SourcingProjectCandidateView) => void;
}) {
  const [activeStage, setActiveStage] = useState<ProjectStage>("sourced");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());
  const [q, setQ] = useState("");

  const rowsFor = (stage: ProjectStage) =>
    sortForDisplay(view.candidates.filter((c) => c.stage === stage));
  // The visible list is the active stage narrowed by the search box. Stage-button
  // counts stay TOTALS (the real stage size) — search only filters what's shown.
  const activeRows = filterProjectRows(rowsFor(activeStage), q);
  const readyCount = activeRows.filter((r) => r.fetchStatus === "ready").length;

  // Selection resolves against the CURRENT list: keep the chosen row if it's
  // still here, otherwise fall back to the top row — never a dangling id.
  const selected = activeRows.find((r) => r.id === selectedId) ?? activeRows[0] ?? null;

  function switchStage(stage: ProjectStage) {
    if (stage === activeStage) return;
    setActiveStage(stage);
    setSelectedId(null); // resolves to the new list's top row
    setChecked(new Set()); // checks are scoped to the visible stage
  }

  function toggleCheck(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Move one row, advancing the selection to the neighbour it leaves behind so
  // the detail pane doesn't snap back to the top after each triage decision.
  function moveOne(row: SourcingProjectCandidateView, stage: ProjectStage) {
    const i = activeRows.findIndex((r) => r.id === row.id);
    const neighbour = activeRows[i + 1] ?? activeRows[i - 1] ?? null;
    setSelectedId(neighbour?.id ?? null);
    setChecked((prev) => {
      if (!prev.has(row.id)) return prev;
      const next = new Set(prev);
      next.delete(row.id);
      return next;
    });
    onMove(row, stage);
  }

  function bulkMove(stage: ProjectStage) {
    const ids = Array.from(checked);
    setChecked(new Set());
    setSelectedId(null);
    for (const id of ids) {
      const row = view.candidates.find((c) => c.id === id);
      if (row) onMove(row, stage);
    }
  }

  return (
    // `shrink-0` is load-bearing: this card's `overflow-hidden` makes it a
    // scroll container, so as a flex item in the page's flex-col `main` its
    // automatic min-height collapses to 0 and flexbox would compress it to fit
    // the viewport — clipping the board's bottom instead of letting the page
    // scroll. Pinning its size forces `main` to overflow and scroll normally.
    <div className="mt-6 shrink-0 overflow-hidden rounded-xl border border-border bg-card">
      {/* Search box (left) + segmented stage switcher. */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/30 px-3 py-2.5">
        <div className="relative mr-1 w-full sm:w-52">
          <Search
            size={13}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar candidatos…"
            aria-label="Buscar candidatos no projeto"
            className="h-8 pl-8 text-[12px]"
          />
        </div>
        {TRIAGE_STAGES.map((stage, i) => {
          const on = stage === activeStage;
          return (
            <Fragment key={stage}>
              {i > 0 && (
                <ArrowRight size={14} className="mx-0.5 shrink-0 text-muted-foreground/40" />
              )}
              <button
                type="button"
                onClick={() => switchStage(stage)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors",
                  on
                    ? "border-border bg-card text-foreground shadow-sm"
                    : "border-transparent text-muted-foreground hover:bg-foreground/5",
                )}
              >
                {STAGE_META[stage].title}
                <span className="font-mono text-[13px] font-bold tabular-nums">
                  {rowsFor(stage).length}
                </span>
              </button>
            </Fragment>
          );
        })}
        <div className="flex-1" />
        <span
          className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
          title="Ordenados por FIT — melhor no topo"
        >
          ↑ FIT
        </span>
        {readyCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => downloadProjectJson(view, [activeStage])}
            title={`Baixar os ${readyCount} candidatos de ${STAGE_META[activeStage].title} em JSON`}
          >
            <Download /> JSON
          </Button>
        )}
      </div>

      {/* Master-detail: ranked list · the selected candidate's full analysis. */}
      <div className="grid md:grid-cols-[minmax(0,24rem)_1fr]">
        <div className="max-h-[36rem] overflow-y-auto border-b border-border md:border-b-0 md:border-r">
          {activeRows.length === 0 ? (
            <p className="px-4 py-6 text-[13px] text-muted-foreground">
              {q.trim()
                ? `Nenhum candidato corresponde a "${q.trim()}".`
                : activeStage === "sourced"
                  ? "Vazio — rode uma sessão ou adicione candidatos manualmente."
                  : "Vazio."}
            </p>
          ) : (
            activeRows.map((row, i) => (
              <TriageRow
                key={row.id}
                row={row}
                rank={i + 1}
                selected={selected?.id === row.id}
                checked={checked.has(row.id)}
                onSelect={() => setSelectedId(row.id)}
                onToggleCheck={() => toggleCheck(row.id)}
              />
            ))
          )}
        </div>

        <div className="max-h-[36rem] overflow-y-auto p-4">
          {selected ? (
            <TriageDetail
              row={selected}
              rank={activeRows.findIndex((r) => r.id === selected.id) + 1}
              onMove={(stage) => moveOne(selected, stage)}
              onRemoveError={() => onRemoveError(selected)}
            />
          ) : (
            <div className="flex h-full min-h-[12rem] items-center justify-center text-[13px] text-muted-foreground">
              Etapa vazia.
            </div>
          )}
        </div>
      </div>

      {/* Bulk-move keybar — appears once rows are checked. */}
      {checked.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2.5">
          <span className="text-[13px] font-medium">
            <span className="text-primary">{checked.size}</span> selecionados
          </span>
          <div className="flex-1" />
          {activeStage !== "shortlisted" && (
            <Button size="sm" onClick={() => bulkMove("shortlisted")}>
              <Star /> Shortlist
            </Button>
          )}
          {activeStage !== "rejected" && (
            <Button size="sm" variant="outline" onClick={() => bulkMove("rejected")}>
              <X /> Rejeitar
            </Button>
          )}
          {activeStage !== "sourced" && (
            <Button size="sm" variant="outline" onClick={() => bulkMove("sourced")}>
              <RotateCcw /> Sourced
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setChecked(new Set())}>
            limpar
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One compact row in the triage list — the scannable summary (rank · name ·
 * location · role line · headline verdict · source · gap count). The checkbox
 * drives the bulk keybar; clicking the body selects the row into the detail
 * pane. Pending/error rows show a spinner/✕ in place of the rank and a status
 * line instead of the verdict chips.
 */
function TriageRow({
  row,
  rank,
  selected,
  checked,
  onSelect,
  onToggleCheck,
}: {
  row: SourcingProjectCandidateView;
  rank: number;
  selected: boolean;
  checked: boolean;
  onSelect: () => void;
  onToggleCheck: () => void;
}) {
  const c = row.candidate;
  const ready = row.fetchStatus === "ready";
  const failed = row.fetchStatus === "error";
  // Line 2 is the role identity only (title · company). Tenure used to ride here too,
  // but a long title/company would crowd it off — it now sits with location below.
  const sub = [c.currentTitle || c.experience[0]?.title, c.experience[0]?.company]
    .filter(Boolean)
    .join(" · ");
  const tenure = formatTenure(c.currentTenureMonths);
  const location = shortLocation(c.location);
  const verdict = row.judgement ? judgeVerdicts(row.judgement)[0] : undefined;
  const gaps = row.judgement?.gaps ?? [];
  const source = SOURCE_META[row.source];
  const SourceIcon = source.icon;

  return (
    <div
      className={cn(
        "flex gap-3 border-b border-l-2 border-border/60 px-3.5 py-3 transition-colors",
        selected
          ? "border-l-primary bg-primary/[0.04]"
          : "border-l-transparent hover:bg-foreground/[0.025]",
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggleCheck}
        aria-label="Selecionar para ação em lote"
        className="mt-0.5 shrink-0"
      />
      <button type="button" onClick={onSelect} className="flex min-w-0 flex-1 gap-3 text-left">
        <span className="mt-0.5 w-6 shrink-0 text-center">
          {ready ? (
            <span className="font-mono text-[13px] font-bold tabular-nums text-primary">
              #{rank}
            </span>
          ) : failed ? (
            <X className="mx-auto h-4 w-4 text-destructive" />
          ) : (
            <Loader2 className="mx-auto h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Name owns its own full-width line — it's the row's identity, so it
              never yields horizontal space to a lower-priority field. */}
          <span className="truncate text-[14px] font-semibold">{c.name || "Perfil sem nome"}</span>
          {sub && <span className="truncate text-[12.5px] text-muted-foreground">{sub}</span>}
          {ready ? (
            <span className="flex flex-col gap-1">
              {/* Line A — where & how long: location · time in current role. Both are
                  low-priority context, so they share a line and truncate/wrap freely. */}
              {(location || tenure) && (
                <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                  {location && (
                    <span
                      className="inline-flex min-w-0 max-w-[9rem] items-center gap-0.5"
                      title={c.location ?? undefined}
                    >
                      <MapPin size={11} className="shrink-0" />
                      <span className="truncate">{location}</span>
                    </span>
                  )}
                  {tenure && (
                    <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                      <Clock size={11} className="shrink-0" />
                      {tenure}
                    </span>
                  )}
                </span>
              )}
              {/* Line B — the judgement: level fit · source · gaps to validate. */}
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {verdict && <Chip tone={verdict.tone}>{verdict.label}</Chip>}
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                  <SourceIcon size={11} />
                  {source.label}
                </span>
                {gaps.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-destructive">
                    <AlertCircle size={12} />
                    {gaps.length} {gaps.length === 1 ? "lacuna" : "lacunas"}
                  </span>
                )}
              </span>
            </span>
          ) : (
            <span className="text-[11.5px] text-muted-foreground">
              {failed
                ? (row.errorMessage ?? "A importação falhou.")
                : row.fetchStatus === "fetching"
                  ? "Importando o perfil…"
                  : "Avaliando contra o brief…"}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

/**
 * The detail pane's body for the selected row. A ready candidate reuses the
 * shared CandidateCard (opened, with the contextual stage buttons in its
 * actions slot) so the "why" reads exactly as it does in the search flows; a
 * pending/error row shows the same PendingRow the list used before.
 */
function TriageDetail({
  row,
  rank,
  onMove,
  onRemoveError,
}: {
  row: SourcingProjectCandidateView;
  rank: number;
  onMove: (stage: ProjectStage) => void;
  onRemoveError: () => void;
}) {
  if (row.fetchStatus === "ready" && row.judgement) {
    const source = SOURCE_META[row.source];
    const SourceIcon = source.icon;
    return (
      // Keyed by row id so selecting a different candidate remounts the card —
      // each newly-opened profile starts expanded, not stuck on the last one's
      // collapse state.
      <CandidateCard
        key={row.id}
        candidate={row.candidate}
        fit={row.judgement}
        rank={rank}
        defaultExpanded
        actions={<StageButtons stage={row.stage} onMove={onMove} />}
        source={
          <span className="inline-flex items-center gap-1">
            <SourceIcon size={12} />
            {source.label}
          </span>
        }
      />
    );
  }
  return <PendingRow row={row} onRemove={onRemoveError} />;
}

/**
 * "Baixar JSON" with a pile CHECKLIST: the recruiter selects which triage
 * groups to export (multi-select, all three pre-checked) and downloads one
 * file with the selection — same per-candidate record as the search export.
 */
function ExportJsonMenu({ view }: { view: SourcingProjectView }) {
  const [selected, setSelected] = useState<Set<ProjectStage>>(
    new Set<ProjectStage>(["sourced", "shortlisted", "rejected"]),
  );
  const readyCount = (stage: ProjectStage) =>
    view.candidates.filter((c) => c.stage === stage && c.fetchStatus === "ready").length;
  const exportable = Array.from(selected).reduce((acc, s) => acc + readyCount(s), 0);

  function toggle(stage: ProjectStage) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <Download /> Baixar JSON
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Grupos a exportar</DropdownMenuLabel>
        {(["sourced", "shortlisted", "rejected"] as const).map((stage) => (
          <DropdownMenuCheckboxItem
            key={stage}
            checked={selected.has(stage)}
            // Keep the menu open while composing the selection.
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(stage)}
          >
            {STAGE_META[stage].title}
            <span className="ml-auto pl-3 font-mono text-[11px] tabular-nums text-muted-foreground">
              {readyCount(stage)}
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={selected.size === 0 || exportable === 0}
          onSelect={() => downloadProjectJson(view, Array.from(selected))}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          Baixar {exportable > 0 && <span className="tabular-nums">({exportable})</span>}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Stage-transition buttons rendered in the card's `actions` slot. */
function StageButtons({
  stage,
  onMove,
}: {
  stage: ProjectStage;
  onMove: (stage: ProjectStage) => void;
}) {
  return (
    <>
      {stage !== "shortlisted" && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onMove("shortlisted")}
          title="Mover para a shortlist"
        >
          <Star /> Shortlist
        </Button>
      )}
      {stage === "shortlisted" && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onMove("sourced")}
          title="Voltar para Sourced"
        >
          <RotateCcw /> Voltar
        </Button>
      )}
      {stage !== "rejected" ? (
        <Button size="sm" variant="ghost" onClick={() => onMove("rejected")} title="Rejeitar">
          <X /> Rejeitar
        </Button>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onMove("sourced")}
          title="Restaurar para Sourced"
        >
          <RotateCcw /> Restaurar
        </Button>
      )}
    </>
  );
}

/** A row still being filled (fetching/judging) or that failed (error). */
function PendingRow({
  row,
  onRemove,
}: {
  row: SourcingProjectCandidateView;
  onRemove: () => void;
}) {
  const failed = row.fetchStatus === "error";
  const label = row.candidate.name || row.candidateId;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
      {failed ? (
        <X className="h-4 w-4 shrink-0 text-destructive" />
      ) : (
        <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
      )}
      <div className="min-w-0 flex-1">
        <a
          href={row.candidate.url}
          target="_blank"
          rel="noreferrer"
          className="block truncate text-[13.5px] font-medium hover:underline"
        >
          {label}
        </a>
        <p className="text-[12px] text-muted-foreground">
          {failed
            ? (row.errorMessage ?? "A importação falhou.")
            : row.fetchStatus === "fetching"
              ? "Importando o perfil do LinkedIn…"
              : "Avaliando contra o brief do projeto…"}
        </p>
      </div>
      {failed && (
        <Button size="sm" variant="ghost" onClick={onRemove} title="Remover para tentar de novo">
          <Trash2 /> Remover
        </Button>
      )}
    </div>
  );
}

/**
 * The recruiter-facing label for a session. The raw brief is the whole JD —
 * long and near-identical across a project's iterations, so it's a poor title.
 * The parsed interpretation is far more distinguishing: role as the headline
 * plus a seniority/level chip. Falls back to the brief when a run has no parsed
 * title (raw-brief runs, or a query that never materialized).
 */
function sessionLabel(session: SourcingExplorationSummary): {
  title: string;
  level: string | null;
} {
  const role = session.query?.filters.title?.trim();
  if (role) {
    const level =
      [session.query?.filters.seniority, session.query?.targetLevelLabel]
        .filter(Boolean)
        .join(" · ") || null;
    return { title: role, level };
  }
  return { title: session.brief, level: null };
}

/** A seniority/level chip matching the QueryInterpretation "Cargo" chip. */
function LevelChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-primary">
      {label}
    </span>
  );
}

/**
 * One session (linked run) row. Collapsed: kind icon, the meaningful title
 * (role + level), status/size/cost, and an open-in-flow link. Expands in place
 * to the full parsed interpretation (the same fact sheet + chips the VAGA card
 * shows) plus the original briefing — so the long JD is one click away, not the
 * title.
 */
function SessionRow({
  session,
  onPick,
}: {
  session: SourcingExplorationSummary;
  /** Open the candidate picker for this run (its results don't auto-ingest). */
  onPick: () => void;
}) {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  const running =
    session.status === "pending" || session.status === "running" || session.status === "harvesting";
  const canPick = session.status === "done" && session.resultCount > 0;
  const { title, level } = sessionLabel(session);
  return (
    <div className="rounded-lg border border-border bg-background transition-colors hover:border-ring">
      <div className="flex items-center gap-3 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDown
            size={14}
            className={`shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`}
          />
          {session.kind === "explore" ? (
            <Sparkles size={14} className="shrink-0 text-muted-foreground" />
          ) : (
            <Target size={14} className="shrink-0 text-muted-foreground" />
          )}
          <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{title}</span>
          {level && <LevelChip label={level} />}
        </button>
        {running && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />}
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {SESSION_STATUS[session.status] ?? session.status} · {session.resultCount} candidatos · $
          {session.usd.toFixed(2)}
        </span>
        {canPick && (
          <Button size="sm" variant="outline" className="shrink-0" onClick={onPick}>
            <UserPlus /> Selecionar candidatos
          </Button>
        )}
        <button
          type="button"
          onClick={() => navigate(`/labs/sourcing?exploration=${session.id}&kind=${session.kind}`)}
          title="Abrir a sessão"
          aria-label="Abrir a sessão"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
        >
          <ExternalLink size={13} />
        </button>
      </div>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {session.query ? (
            <QueryInterpretation query={session.query} eyebrow="O que esta sessão procurou" />
          ) : (
            <>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                Briefing original
              </span>
              <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-foreground/80">
                {session.brief}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Empresas bloqueadas — the per-project company block list. A candidate whose
 * CURRENT employer is listed never enters the project (enforced at the bulk ingest
 * chokepoints). The per-position config overrides live on the separate "Customizar
 * Projeto" screen; saving here sends only `blockedCompanies` (partial patch, so the
 * overrides are untouched). Folded by default so triage stays the page's focus.
 */
function ProjectSettingsCard({
  view,
  projectId,
  onSaved,
}: {
  view: SourcingProjectView;
  projectId: string;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [blocked, setBlocked] = useState<BlockedCompanyInput[]>(() =>
    view.config.blockedCompanies.map((b) => ({
      name: b.name,
      ...(b.companyId ? { companyId: b.companyId } : {}),
      ...(b.linkedinUrl ? { linkedinUrl: b.linkedinUrl } : {}),
    })),
  );
  const [companyInput, setCompanyInput] = useState("");

  function addCompany() {
    const name = companyInput.trim();
    if (name && !blocked.some((b) => b.name.toLowerCase() === name.toLowerCase())) {
      setBlocked((prev) => [...prev, { name }]);
    }
    setCompanyInput("");
  }

  async function save() {
    setSaving(true);
    try {
      // Only the block list — per-position overrides live on the "Customizar
      // Projeto" screen. Omitting `overrides` leaves them untouched (partial patch).
      await rpcClient.labs.sourcingProjectUpdate({
        projectId,
        config: { blockedCompanies: blocked },
      });
      toast({ title: "Empresas bloqueadas salvas" });
      onSaved();
    } catch (err) {
      toast({
        title: "Falha ao salvar",
        description: err instanceof Error ? err.message : "Erro inesperado",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="mt-6 rounded-xl border border-border bg-card"
    >
      <CollapsibleTrigger className="group flex w-full items-center gap-2.5 px-5 py-4 text-left">
        <ChevronDown
          size={15}
          className="shrink-0 text-muted-foreground transition-transform group-data-[state=closed]:-rotate-90"
        />
        <Ban size={14} className="shrink-0 text-muted-foreground" />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          Empresas bloqueadas
        </span>
        {!open && blocked.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Ban size={11} /> {blocked.length}{" "}
            {blocked.length === 1 ? "empresa bloqueada" : "empresas bloqueadas"}
          </span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-6 px-5 pb-5">
        {/* Company blocking — the headline per-project control. */}
        <div>
          <div className="flex items-center gap-2">
            <Ban size={13} className="text-muted-foreground" />
            <span className="text-[12px] font-semibold">Empresas bloqueadas</span>
          </div>
          <p className="mt-1 text-[12px] text-muted-foreground">
            Candidatos cujo <span className="font-medium">empregador atual</span> estiver na lista
            não entram no projeto (vale para sessões e para trazer de uma exploração).
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {blocked.length === 0 ? (
              <span className="text-[12px] italic text-muted-foreground/70">
                Nenhuma empresa bloqueada.
              </span>
            ) : (
              blocked.map((b) => (
                <span
                  key={b.name}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[12px]"
                >
                  {b.name}
                  <button
                    type="button"
                    onClick={() => setBlocked((prev) => prev.filter((x) => x !== b))}
                    aria-label={`Remover ${b.name}`}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))
            )}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              value={companyInput}
              onChange={(e) => setCompanyInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCompany();
                }
              }}
              placeholder="Nome da empresa (ex.: Nubank)"
              maxLength={200}
              className="h-8 max-w-xs text-[13px]"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={addCompany}
              disabled={!companyInput.trim()}
            >
              <Plus /> Adicionar
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button size="sm" onClick={() => void save()} disabled={saving}>
            {saving ? <Loader2 className="animate-spin" /> : <Check />} Salvar
          </Button>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * The manual-URL adds, as one synthetic session card. Those candidates have no
 * run of their own (the recruiter pasted LinkedIn URLs), so nothing in the
 * strip represented them before. Collapsed: count, Apify spend, and in-flight
 * state; expands to the individual profiles with their fill/error status.
 */
function ManualAddsCard({ rows, usd }: { rows: SourcingProjectCandidateView[]; usd: number }) {
  const [open, setOpen] = useState(false);
  const fetching = rows.filter(
    (r) => r.fetchStatus === "fetching" || r.fetchStatus === "judging",
  ).length;
  const errored = rows.filter((r) => r.fetchStatus === "error").length;
  return (
    <div className="rounded-lg border border-border bg-background transition-colors hover:border-ring">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-2 text-left"
      >
        <ChevronDown
          size={14}
          className={`shrink-0 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`}
        />
        <Link2 size={14} className="shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium">Adições por URL</span>
        {fetching > 0 && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
        )}
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {rows.length} {rows.length === 1 ? "perfil" : "perfis"}
          {errored > 0 && <span className="text-destructive"> · {errored} com erro</span>} · $
          {usd.toFixed(2)}
        </span>
      </button>
      {open && (
        <ul className="border-t border-border px-4 py-2">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-2 py-1">
              {r.fetchStatus === "error" ? (
                <X className="h-3.5 w-3.5 shrink-0 text-destructive" />
              ) : r.fetchStatus === "ready" ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-muted-foreground" />
              )}
              <a
                href={r.candidate.url}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 flex-1 truncate text-[12.5px] hover:underline"
              >
                {r.candidate.name || r.candidateId}
              </a>
              <span className="shrink-0 text-[11px] capitalize text-muted-foreground">
                {STAGE_META[r.stage].title}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
